import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * A finger / mouse / pen signature box on a canvas.
 *
 * - Pointer events (iPhone Safari 13+, Android Chrome, desktop), touch-action: none on the canvas
 *   so a stroke never scrolls the page; pointer capture so a stroke that leaves the box keeps
 *   drawing; pointercancel ends the stroke (what was drawn stays).
 * - Sharp on any screen: the bitmap is css size × devicePixelRatio.
 * - Strokes are kept as points, so a rotation / resize redraws them (scaled to fit, never
 *   stretched) instead of losing the signature.
 * - Ref: clear(), isEmpty(), toPngBase64() (white background, no "data:" prefix, ≤ 1000 px wide).
 */

const INK = '#0f172a';
const LINE_CSS_PX = 2.6;
export const EXPORT_MAX_WIDTH = 1000;

/** A stroke counts as ink once it has 2 points (a lone tap is not a signature). */
export function hasInk(strokes) {
  return Array.isArray(strokes) && strokes.some((s) => Array.isArray(s) && s.length >= 2);
}

/** Uniform scale from the size the strokes were drawn at to the box as it is now. */
export function fitScale(base, size) {
  if (!base || !size || !base.w || !base.h || !size.w || !size.h) return 1;
  return Math.min(size.w / base.w, size.h / base.h);
}

function drawStrokes(ctx, strokes, scale, lineWidth) {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.lineWidth = lineWidth;
  for (const pts of strokes) {
    if (!pts.length) continue;
    if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0][0] * scale, pts[0][1] * scale, lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(pts[0][0] * scale, pts[0][1] * scale);
    for (let i = 1; i < pts.length - 1; i += 1) {
      const mx = ((pts[i][0] + pts[i + 1][0]) / 2) * scale;
      const my = ((pts[i][1] + pts[i + 1][1]) / 2) * scale;
      ctx.quadraticCurveTo(pts[i][0] * scale, pts[i][1] * scale, mx, my);
    }
    const last = pts[pts.length - 1];
    ctx.lineTo(last[0] * scale, last[1] * scale);
    ctx.stroke();
  }
}

const SignaturePad = forwardRef(function SignaturePad(
  { height = 160, onChange, ariaLabel = 'Signature box. Sign with your finger.', disabled = false },
  ref,
) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]); // [[ [x, y], ... ], ...] in BASE coordinates
  const baseRef = useRef(null); // {w, h}: the css size when the first stroke after a clear began
  const sizeRef = useRef({ w: 0, h: 0 });
  const activeRef = useRef(null); // {pointerId, stroke}
  const inkRef = useRef(false);

  const notify = useCallback(() => {
    const now = hasInk(strokesRef.current);
    if (now !== inkRef.current) {
      inkRef.current = now;
      if (onChange) onChange(now);
    }
  }, [onChange]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return;
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, sizeRef.current.w, sizeRef.current.h);
    drawStrokes(ctx, strokesRef.current, fitScale(baseRef.current, sizeRef.current), LINE_CSS_PX);
  }, []);

  // Size the bitmap to the box (× devicePixelRatio) and redraw: on mount, rotation, resize.
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const bw = Math.max(1, Math.round(w * dpr));
    const bh = Math.max(1, Math.round(h * dpr));
    if (w === sizeRef.current.w && h === sizeRef.current.h && canvas.width === bw && canvas.height === bh) return;
    sizeRef.current = { w, h };
    canvas.width = bw;
    canvas.height = bh;
    redraw();
  }, [redraw]);

  useEffect(() => {
    fit();
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    let ro = null;
    if (typeof ResizeObserver === 'function' && canvasRef.current) {
      ro = new ResizeObserver(onResize);
      ro.observe(canvasRef.current);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (ro) ro.disconnect();
    };
  }, [fit]);

  const pointAt = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (!baseRef.current || !hasStrokes()) baseRef.current = { w: rect.width || 1, h: rect.height || 1 };
    const s = fitScale(baseRef.current, { w: rect.width, h: rect.height }) || 1;
    const x = (Number(e.clientX) || 0) - rect.left;
    const y = (Number(e.clientY) || 0) - rect.top;
    return [Math.round((x / s) * 10) / 10, Math.round((y / s) * 10) / 10];
  };

  function hasStrokes() {
    return strokesRef.current.length > 0;
  }

  const onPointerDown = (e) => {
    if (disabled) return;
    if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;
    if (activeRef.current) return; // one finger at a time
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas && canvas.setPointerCapture && e.pointerId !== undefined) {
      try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* old browsers */ }
    }
    if (sizeRef.current.w === 0) fit();
    const stroke = [pointAt(e)];
    strokesRef.current.push(stroke);
    activeRef.current = { pointerId: e.pointerId, stroke };
    redraw();
  };

  const onPointerMove = (e) => {
    const active = activeRef.current;
    if (!active || (e.pointerId !== undefined && active.pointerId !== undefined && e.pointerId !== active.pointerId)) return;
    e.preventDefault();
    const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
    const list = events && events.length ? events : [e];
    for (const ev of list) {
      const p = pointAt(ev);
      const last = active.stroke[active.stroke.length - 1];
      if (!last || last[0] !== p[0] || last[1] !== p[1]) active.stroke.push(p);
    }
    redraw();
    notify();
  };

  const endStroke = (e) => {
    const active = activeRef.current;
    if (!active || (e && e.pointerId !== undefined && active.pointerId !== undefined && e.pointerId !== active.pointerId)) return;
    activeRef.current = null;
    const canvas = canvasRef.current;
    if (canvas && canvas.releasePointerCapture && e && e.pointerId !== undefined) {
      try { canvas.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
    }
    notify();
  };

  useImperativeHandle(ref, () => ({
    clear() {
      strokesRef.current = [];
      baseRef.current = null;
      activeRef.current = null;
      redraw();
      notify();
    },
    isEmpty() {
      return !hasInk(strokesRef.current);
    },
    /** White background, the strokes only, base64 without the data: prefix. null if empty. */
    toPngBase64() {
      if (!hasInk(strokesRef.current)) return null;
      const base = baseRef.current || sizeRef.current;
      const k = Math.min(2, EXPORT_MAX_WIDTH / Math.max(1, base.w));
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(base.w * k));
      out.height = Math.max(1, Math.round(base.h * k));
      const ctx = out.getContext ? out.getContext('2d') : null;
      if (!ctx) return null;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, out.width, out.height);
      drawStrokes(ctx, strokesRef.current, k, LINE_CSS_PX * k);
      const url = out.toDataURL('image/png');
      const i = String(url || '').indexOf(',');
      return i >= 0 ? url.slice(i + 1) : null;
    },
    strokeCount() {
      return strokesRef.current.length;
    },
  }), [redraw, notify]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={ariaLabel}
      data-testid="signature-pad"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endStroke}
      onPointerCancel={endStroke}
      onLostPointerCapture={endStroke}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        display: 'block',
        width: '100%',
        height,
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        cursor: disabled ? 'not-allowed' : 'crosshair',
        background: '#fff',
        borderRadius: 10,
      }}
    />
  );
});

export default SignaturePad;
